import { getApiHost, ensureSpaToken, getSpaToken } from '../utils/api';
import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = getApiHost();

type Room = 'watchlist' | 'performers' | 'bundles' | 'prices' | 'trending';

export interface PriceUpdate {
  mint: string;
  price: number;      // USD
  priceSol: number;   // SOL (for multiplier calculation)
  marketCap: number;  // USD
  timestamp: number;
}

/** Diff payload from the FrontPageListManager */
export interface ListDiff<T = any> {
  added: T[];
  removed: string[];  // array of mint addresses
}

interface UseSocketUpdatesOptions {
  rooms: Room[];
  /** Full snapshot of the watchlist (sent on connect and every scan cycle) */
  onWatchlistSnapshot?: (items: any[]) => void;
  /** Incremental diff: tokens added/removed from the watchlist */
  onWatchlistDiff?: (diff: ListDiff) => void;
  /** Full snapshot of the performers list */
  onPerformersSnapshot?: (items: any[]) => void;
  /** Incremental diff: tokens added/removed from performers */
  onPerformersDiff?: (diff: ListDiff) => void;
  onBundlesUpdate?: (items: any[]) => void;
  onPriceUpdate?: (updates: PriceUpdate[]) => void;
  onTrendingSnapshot?: (payload: { view: string; tokens: any[]; cachedAt: number }) => void;
}

export function useSocketUpdates({
  rooms,
  onWatchlistSnapshot,
  onWatchlistDiff,
  onPerformersSnapshot,
  onPerformersDiff,
  onBundlesUpdate,
  onPriceUpdate,
  onTrendingSnapshot,
}: UseSocketUpdatesOptions) {
  const socketRef = useRef<Socket | null>(null);

  // Stable callback refs
  const watchlistSnapshotCb = useRef(onWatchlistSnapshot);
  const watchlistDiffCb = useRef(onWatchlistDiff);
  const performersSnapshotCb = useRef(onPerformersSnapshot);
  const performersDiffCb = useRef(onPerformersDiff);
  const bundlesCb = useRef(onBundlesUpdate);
  const priceCb = useRef(onPriceUpdate);
  const trendingSnapshotCb = useRef(onTrendingSnapshot);

  watchlistSnapshotCb.current = onWatchlistSnapshot;
  watchlistDiffCb.current = onWatchlistDiff;
  performersSnapshotCb.current = onPerformersSnapshot;
  performersDiffCb.current = onPerformersDiff;
  bundlesCb.current = onBundlesUpdate;
  priceCb.current = onPriceUpdate;
  trendingSnapshotCb.current = onTrendingSnapshot;

  useEffect(() => {
    let socket: Socket | null = null;
    let cancelled = false;

    // Ensure SPA token is ready before connecting
    ensureSpaToken().then(() => {
      if (cancelled) return;

      socket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        withCredentials: true,
        auth: { spaToken: getSpaToken() },
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('[WS] Connected:', socket!.id);
        socket!.emit('subscribe', rooms);
      });

      // ── Front-page list events ────────────────────────────────────────

      // Snapshots — sent on subscribe and periodically (full replacement)
      socket.on('frontpage:watchlist:snapshot', (items: any[]) => {
        watchlistSnapshotCb.current?.(items);
      });

      socket.on('frontpage:performers:snapshot', (items: any[]) => {
        performersSnapshotCb.current?.(items);
      });

      // Full update — sent every scan cycle (full replacement with fresh prices)
      socket.on('frontpage:watchlist:update', (items: any[]) => {
        watchlistSnapshotCb.current?.(items);
      });

      socket.on('frontpage:performers:update', (items: any[]) => {
        performersSnapshotCb.current?.(items);
      });

      // Diffs — sent when tokens are added/removed from the lists
      socket.on('frontpage:watchlist:diff', (diff: ListDiff) => {
        watchlistDiffCb.current?.(diff);
      });

      socket.on('frontpage:performers:diff', (diff: ListDiff) => {
        performersDiffCb.current?.(diff);
      });

      // ── Other events ──────────────────────────────────────────────────

      socket.on('bundles:update', (items: any[]) => {
        bundlesCb.current?.(items);
      });

      socket.on('price:update', (updates: PriceUpdate[]) => {
        priceCb.current?.(updates);
      });

      // ── Trending events ──────────────────────────────────────────────

      socket.on('trending:snapshot', (payload: { view: string; tokens: any[]; cachedAt: number }) => {
        trendingSnapshotCb.current?.(payload);
      });

      socket.on('disconnect', (reason) => {
        console.log('[WS] Disconnected:', reason);
      });
    });

    return () => {
      cancelled = true;
      if (socket) {
        socket.disconnect();
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]); // reconnect if rooms change
}
